import React, { useCallback, useEffect, useRef, useState } from "react"
import styled, { useTheme } from "~/ui/styled"
import { observer } from "mobx-react-lite"
import useNavigation from "~hooks/useNavigation"
import { useLocation } from "react-router-dom"
import { IconFont, Button, Text, CenterFlex } from "~components/El"
import { useStores } from "~models"
import { storeNoticeReturnPath } from "~ui/app-shell"
import logo from "~assets/images/app-intro-logo.png"

const DATA = [
  { title: "创建群聊", icon: "cjql", link: "AddGroupChat" },
  { title: "创建频道", icon: "cjpd", link: "AddChannel" },
  { title: "添加好友", icon: "tjhy", link: "AddContact" },
]

const Container = styled(CenterFlex)`
  flex-direction: row;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`

const MenuPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 8px;
  padding: 6px 0;
  min-width: 148px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 10;
`

const MenuArrow = styled.div`
  width: 10px;
  height: 10px;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  transform: rotate(45deg);
  position: absolute;
  right: 14px;
  top: -5px;
  box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.04);
`

const MenuItem = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
  box-sizing: border-box;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grayBackground};
  }
`

const LogoWrap = styled.div`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.secondary};

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

function QuickActionMenu({
  onSelect,
}: {
  onSelect: (link: string) => void
}) {
  const theme = useTheme()
  return (
    <MenuPanel>
      <MenuArrow />
      {DATA.map((item) => (
        <MenuItem key={item.link} type="button" onClick={() => onSelect(item.link)}>
          <IconFont name={item.icon} size={16} color={theme.colors.text} />
          <Text color={theme.colors.text} fontSize="14px" ml="10px">
            {item.title}
          </Text>
        </MenuItem>
      ))}
    </MenuPanel>
  )
}

export const Dropdown = observer(() => {
  const theme = useTheme()
  const { notificationStore } = useStores()
  const unreadCount = notificationStore.userUnreadNotificationCount
  const navigation = useNavigation()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const onSelect = useCallback(
    (link: string) => {
      closeMenu()
      navigation.navigate(link)
    },
    [closeMenu, navigation],
  )

  useEffect(() => {
    if (!menuOpen) return

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeMenu()
      }
    }

    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [closeMenu, menuOpen])

  return (
    <Container ref={containerRef}>
      <Button
        height="30px"
        padding="0 10px"
        style={{ position: "relative" }}
        onClick={() => {
          storeNoticeReturnPath(location.pathname)
          navigation.navigate("NoticeCenter")
        }}
      >
        <IconFont name="sykq" size={18} color={theme.colors.text} />
        {unreadCount > 0 ? (
          <CenterFlex
            position="absolute"
            right="10px"
            top="4px"
            width="6px"
            height="6px"
            borderRadius="3px"
            bg={theme.colors.danger}
          />
        ) : null}
      </Button>
      <Button
        width="30px"
        height="30px"
        borderRadius="15px"
        backgroundColor={theme.colors.background}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <IconFont name="jh" size={14} color={theme.colors.text} />
      </Button>
      {menuOpen ? <QuickActionMenu onSelect={onSelect} /> : null}
    </Container>
  )
})

export const LogoHeader = observer(() => {
  const theme = useTheme()
  const { configStore } = useStores()
  return (
    <CenterFlex flexDirection="row">
      <LogoWrap>
        <img src={logo} alt="" />
      </LogoWrap>
      <Text color={theme.colors.text} fontSize="15px" fontWeight={700} ml="6px" style={{ letterSpacing: "0.5px" }}>
        {configStore.appName || "LOGO"}
      </Text>
    </CenterFlex>
  )
})
